import React, { useState, useEffect, useContext, useRef } from 'react';
import {
	Modal,
	Button,
	Form,
	Row,
	Col,
	Alert,
	Spinner,
	Badge,
	Card,
	Nav,
	Container,
	InputGroup,
	Table,
} from 'react-bootstrap';
import AuthContext from '../../../contexts/authContext';
import CreateEditDevisModal from './DevisModal';
import showNotification from '../../../components/extras/showNotification';
import { deletePythonAPI, paiementAPI, searchPythonAPI } from '../../../apis/backend/apiPython';
import Icon from '../../../components/icon/Icon';
import { putAPI, deleteAPI, postAPI } from '../../../apis/backend/apiNode';

// --- CONSTANTS ---
const PAIEMENT_STATUTS = {
	EN_ATTENTE: 'en_attente',
	VALIDE: 'validé',
	REFUSE: 'refusé',
	REMBOURSE: 'remboursé',
};

const METHODES_PAIEMENT = {
	ESPECES: 'espèces',
	CARTE_BANCAIRE: 'carte_bancaire',
	CHEQUE: 'chèque',
	VIREMENT: 'virement',
	AUTRE: 'autre',
};

// Fonctions utilitaires pour gérer les devis
const getDevisId = (devis) => {
	if (!devis) return null;
	if (typeof devis === 'string') return devis;
	if (devis.id) return devis.id;
	if (devis._id) return devis._id;
	return null;
};

const getDevisName = (devis) => {
	if (!devis) return 'Devis inconnu';
	if (typeof devis === 'string') {
		return `Devis #${devis.substring(0, 8)}`;
	}
	if (devis.name) return devis.name;
	if (devis.reference) return devis.reference;
	const devisId = getDevisId(devis);
	if (devisId && typeof devisId === 'string') {
		return `Devis #${devisId.substring(0, 8)}`;
	}
	return 'Devis #unknown';
};

const RapportModal = ({
	show,
	onHide,
	intervention,
	rapport, // This is rapport_intervention from the intervention
	onSave,
	onDelete,
	isEditing = false,
	reloadData,
}) => {
	const { userData } = useContext(AuthContext);

	// --- STATES ---
	const [formData, setFormData] = useState({
		description_travaux: '',
		devis: [],
		pose_faite: false,
		pose_sur_place: false
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [journals, setJournals] = useState([]);
	const [paiements, setPaiements] = useState([]);

	// State pour les pièces jointes
	const [attachments, setAttachments] = useState([]);
	const [filesToUpload, setFilesToUpload] = useState([]);
	const [deletingAttachment, setDeletingAttachment] = useState(null);
	const [uploadingAttachments, setUploadingAttachments] = useState(false);
	const [createdRapportId, setCreatedRapportId] = useState(null);

	// State pour les devis
	const [devisList, setDevisList] = useState([]);
	const [availableDevis, setAvailableDevis] = useState([]);

	// Modal Paiement States
	const [showPaiementModal, setShowPaiementModal] = useState(false);
	const [editingPaiement, setEditingPaiement] = useState(null);
	const [paiementForm, setPaiementForm] = useState({
		montant: '',
		methode_paiement: METHODES_PAIEMENT.ESPECES,
		reference: '',
		statut: PAIEMENT_STATUTS.EN_ATTENTE,
		notes: '',
		payment_method: '',
		date_payment: new Date().toISOString().split('T')[0],
	});
	const [submittingPaiement, setSubmittingPaiement] = useState(false);

	// Modal Devis States
	const [showDevisModal, setShowDevisModal] = useState(false);
	const [selectedDevisToEdit, setSelectedDevisToEdit] = useState(null);

	// Active tab state
	const [activeTab, setActiveTab] = useState('general');

	// Helper function to get company ID
	const getCompanyIdFromIntervention = (intervention) => {
		if (!intervention?.cars?.[0]?.companyId) {
			return userData?.company_id;
		}
		return intervention.cars[0].companyId || userData?.company_id;
	};

	// --- EFFECTS ---
	useEffect(() => {
		if (show) {
			if (intervention?.client?._id) {
				loadAvailableDevis(intervention.client._id);
			}

			if (isEditing && rapport) {
				if (rapport.attachments && Array.isArray(rapport.attachments)) {
					setAttachments(rapport.attachments);
				}

				if (rapport.devis && Array.isArray(rapport.devis)) {
					const initialDevisList = rapport.devis.map((devisItem) => {
						const devisId = getDevisId(devisItem);
						return {
							id: devisId,
							name: getDevisName(devisItem),
							totalTTC: devisItem.totalTTC || 0,
							dateValidite: devisItem.dateValidite,
							statut: devisItem.statut || 'inconnu',
						};
					});
					setDevisList(initialDevisList);
				}

				if (rapport.paiements) {
					setPaiements(rapport.paiements);
				}
			}
		} else {
			setCreatedRapportId(null);
			setSelectedDevisToEdit(null);
		}
	}, [show, isEditing, rapport, intervention?.client?._id]);

	useEffect(() => {
		if (rapport && isEditing) {
			setFormData({
				description_travaux: rapport.description_travaux || '',
				devis: rapport.devis || [],
				pose_faite: rapport.pose_faite || false,
				pose_sur_place: rapport.pose_sur_place || false,
			});

			if (rapport.paiements) {
				setPaiements(rapport.paiements);
			}

			if (rapport.attachments && Array.isArray(rapport.attachments)) {
				setAttachments(rapport.attachments);
			}
		} else {
			resetForm();
		}
	}, [rapport, isEditing]);

	// --- HELPER FUNCTIONS ---
	const loadAvailableDevis = async (clientId) => {
		try {
			const response = await searchPythonAPI('devis', {
				and_conditions: [
					{ field: 'clientId', operator: '=', value: clientId },
					{
						field: 'statut',
						operator: 'in',
						value: ['validé', 'en_attente', 'accepté', 'brouillon'],
					},
				],
			});

			if (response?.data) {
				setAvailableDevis(response.data);

				if (isEditing && rapport?.devis?.length > 0) {
					const existingDevis = response.data.filter((devis) =>
						rapport.devis.some((d) => {
							const rapportDevisId = getDevisId(d);
							return rapportDevisId === devis.id;
						}),
					);
					setDevisList(existingDevis);
				}
			}
		} catch (error) {
			console.error('❌ Erreur chargement devis:', error);
		}
	};

	const handleAddDevis = (devis) => {
		const devisId = getDevisId(devis);
		if (!devisList.some((d) => getDevisId(d) === devisId)) {
			setDevisList((prev) => [...prev, devis]);
		}
	};

	const handleRemoveDevis = (devisId) => {
		setDevisList((prev) => prev.filter((d) => getDevisId(d) !== devisId));
	};

	const handleEditDevis = (devis) => {
		const devisId = getDevisId(devis);
		if (devisId) {
			setSelectedDevisToEdit(devis);
			setShowDevisModal(true);
		}
	};

	const resetForm = () => {
		setFormData({ description_travaux: '', devis: [] });
		setPaiements([]);
		setDevisList([]);
		setAttachments([]);
		setFilesToUpload([]);
		setActiveTab('general');
		setCreatedRapportId(null);
	};

	// Fonctions pour les pièces jointes
	const handleFileChange = (e) => {
		const files = Array.from(e.target.files);
		setFilesToUpload((prev) => [...prev, ...files]);
	};

	const removeFileFromUpload = (index) => {
		setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
	};

	/*   const uploadAttachments = async () => {
	if (filesToUpload.length === 0) {
	  return { success: true, uploadedFiles: [] };
	}

	setUploadingAttachments(true);
	try {
	  // Since we're embedding in intervention, we need to update intervention with attachments
	  const formattedFiles = filesToUpload.map((file) => ({
		originalName: file.name,
		fileName: `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${file.name}`,
		mimeType: file.type || 'application/octet-stream',
		size: file.size || 0,
		path: `uploads/intervention/${intervention?._id}/${file.name}`,
		uploadedAt: new Date().toISOString(),
		uploadedBy: userData._id || userData.id,
	  }));

	  const newAttachments = [...attachments, ...formattedFiles];
	  setAttachments(newAttachments);
	  setFilesToUpload([]);

	  return {
		success: true,
		uploadedFiles: formattedFiles,
	  };
	} catch (error) {
	  return {
		success: false,
		error: `Erreur lors du traitement des fichiers: ${error.message}`,
	  };
	} finally {
	  setUploadingAttachments(false);
	}
  }; */

	const uploadAttachments = async (rapportId) => {
		if (filesToUpload.length === 0) {
			return { success: true, uploadedFiles: [] };
		}

		setUploadingAttachments(true);
		try {
			const formData = new FormData();
			filesToUpload.forEach((file) => {
				formData.append('attachments', file);
			});

			const uploadResponse = await postAPI(
				'attachment',
				formData,
				`/rapport/${intervention?._id}`,
			);

			if (uploadResponse?.files) {
				const formattedFiles = uploadResponse.files.map((file) => ({
					originalName: file.originalName || file.name,
					fileName:
						file.fileName ||
						`att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
					mimeType: file.mimeType || file.type || 'application/octet-stream',
					size: file.size || 0,
					path:
						file.path ||
						`uploads/rapport/${intervention?._id}/${file.fileName || file.name}`,
					uploadedAt: new Date().toISOString(),
					uploadedBy: userData._id || userData.id,
				}));

				const updateResponse = await putAPI(
					'planning',
					{
						operation: 'add',
						newAttachments: formattedFiles,
						userId: userData._id || userData.id,
					},
					`/intervention/${intervention?._id}/attachments`,
				);

				if (updateResponse?.success) {
					const newAttachments = [...attachments, ...formattedFiles];
					setAttachments(newAttachments);
					setFilesToUpload([]);

					return {
						success: true,
						uploadedFiles: formattedFiles,
					};
				} else {
					return {
						success: false,
						error: 'Échec de la mise à jour des pièces jointes',
					};
				}
			} else {
				return {
					success: false,
					error: 'Échec du téléchargement des pièces jointes',
				};
			}
		} catch (error) {
			return {
				success: false,
				error: `Erreur lors du téléchargement: ${error.message}`,
			};
		} finally {
			setUploadingAttachments(false);
		}
	};

	const handleUploadPendingAttachments = async () => {
		const result = await uploadAttachments();
		if (result.success) {
			showNotification('Succès', 'Fichiers ajoutés avec succès', 'success');
			return true;
		} else {
			showNotification('Erreur', result.error || 'Échec du téléchargement', 'danger');
			return false;
		}
	};

	const deleteAttachment = async (attachmentId, attachmentIndex) => {
		if (!attachmentId || !intervention?._id) return;

		setDeletingAttachment(attachmentId);
		try {
			await deleteAPI('attachment', `/${attachmentId}`);

			const deleteResponse = await deleteAPI(
				'planning',
				`/${intervention?._id}/attachments/${attachmentId}`,
				{
					userId: userData._id || userData.id,
				},
			);

			if (deleteResponse?.success) {
				const newAttachments = attachments.filter(
					(att, idx) => idx !== attachmentIndex && att._id !== attachmentId,
				);

				setAttachments(newAttachments);
				showNotification('Succès', 'Fichier supprimé avec succès', 'success');
			} else {
				throw new Error('Failed to delete attachment from intervention');
			}
		} catch (e) {
			try {
				const newAttachments = attachments.filter(
					(att, idx) => idx !== attachmentIndex && att._id !== attachmentId,
				);

				const updateResponse = await putAPI(
					'planning',
					{
						operation: 'replace',
						attachments: newAttachments,
						userId: userData._id || userData.id,
					},
					`/intervention/${intervention?._id}/attachments`,
				);

				if (updateResponse?.success) {
					setAttachments(newAttachments);
					showNotification('Succès', 'Fichier supprimé avec succès', 'success');
				} else {
					throw new Error('Fallback also failed');
				}
			} catch (fallbackError) {
				showNotification('Erreur', 'Échec de la suppression du fichier', 'danger');
			}
		} finally {
			setDeletingAttachment(null);
		}
	};

	const getFileUrl = (attachment) => {
		if (attachment.path && !attachment.path.startsWith('blob:')) {
			return `/coreService/${attachment.path}`;
		}
		return '#';
	};

	const renderFileItem = (file, index, isUpload = false) => {
		const isImage = file.type?.startsWith('image/') || file.mimeType?.startsWith('image/');
		const fileName = file.originalName || file.name || 'Fichier sans nom';
		const fileSize = file.size ? `${(file.size / 1024).toFixed(1)} KB` : '';

		let fileUrl;
		if (isUpload) {
			fileUrl = URL.createObjectURL(file);
		} else {
			fileUrl = getFileUrl(file);
		}

		return (
			<div className='position-relative'>
				<Card className='border-0 shadow-sm h-100 gallery-card'>
					<Card.Body className='p-0'>
						<div className='ratio ratio-4x3 bg-light rounded-top'>
							{isImage ? (
								<img
									src={fileUrl}
									alt={fileName}
									className='w-100 h-100 object-fit-cover rounded-top'
									style={{ objectFit: 'cover' }}
									onError={(e) => {
										e.target.onerror = null;
										e.target.src = `data:image/svg+xml;base64,${btoa(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200">
                        <rect width="300" height="200" fill="#f8f9fa"/>
                        <text x="150" y="110" font-family="Arial" font-size="14" text-anchor="middle" fill="#6c757d">
                          Image non disponible
                        </text>
                      </svg>
                    `)}`;
									}}
									crossOrigin='anonymous'
								/>
							) : (
								<div className='d-flex flex-column align-items-center justify-content-center h-100 bg-light rounded-top'>
									<Icon
										icon='InsertDriveFile'
										size='2x'
										className='text-muted mb-2'
									/>
									<small
										className='text-muted text-center px-2'
										style={{ fontSize: '0.7rem' }}>
										{fileName.split('.').pop()?.toUpperCase() || 'FILE'}
									</small>
								</div>
							)}
						</div>
						<div className='p-3'>
							<div className='fw-medium text-truncate small' title={fileName}>
								{fileName}
							</div>
							<div className='small text-muted'>
								{fileSize}
								{!isUpload && file.uploadedAt && (
									<span className='ms-2'>
										{new Date(file.uploadedAt).toLocaleDateString('fr-FR')}
									</span>
								)}
							</div>
						</div>
					</Card.Body>

					<div className='position-absolute top-0 end-0 m-2'>
						{isUpload ? (
							<Button
								variant='danger'
								size='sm'
								className='rounded-circle p-1'
								style={{ width: '28px', height: '28px' }}
								onClick={() => removeFileFromUpload(index)}
								title='Supprimer'>
								<Icon icon='Close' size='sm' />
							</Button>
						) : (
							<Button
								variant='danger'
								size='sm'
								className='rounded-circle p-1'
								style={{ width: '28px', height: '28px' }}
								onClick={() => deleteAttachment(file._id || file.path, index)}
								disabled={deletingAttachment === (file._id || file.path)}
								title='Supprimer'>
								{deletingAttachment === (file._id || file.path) ? (
									<Spinner size='sm' animation='border' />
								) : (
									<Icon icon='Delete' size='sm' />
								)}
							</Button>
						)}
					</div>
				</Card>
			</div>
		);
	};

	// --- GESTION PAIEMENTS ---
	const handleShowPaiementModal = (paiement = null) => {
		if (paiement) {
			setEditingPaiement(paiement);
			setPaiementForm({
				montant: paiement.montant?.toString() || '',
				methode_paiement: paiement.methode_paiement || METHODES_PAIEMENT.ESPECES,
				reference: paiement.reference || '',
				statut: paiement.statut || PAIEMENT_STATUTS.EN_ATTENTE,
				notes: paiement.notes || '',
				payment_method: paiement.payment_method || '',
				date_payment: paiement.date_payment || new Date().toISOString().split('T')[0],
			});
		} else {
			setEditingPaiement(null);
			setPaiementForm({
				montant: '',
				methode_paiement: METHODES_PAIEMENT.ESPECES,
				reference: '',
				statut: PAIEMENT_STATUTS.EN_ATTENTE,
				notes: '',
				payment_method: '',
				date_payment: new Date().toISOString().split('T')[0],
			});
		}
		setShowPaiementModal(true);
	};

	const handleSavePaiement = async () => {
		if (!paiementForm.montant || parseFloat(paiementForm.montant) <= 0) {
			showNotification('Erreur', 'Le montant doit être supérieur à 0', 'warning');
			return;
		}

		setSubmittingPaiement(true);
		setError('');

		try {
			const montant = parseFloat(paiementForm.montant);
			let pythonPaymentId = null;

			if (paiementForm.methode_paiement) {
				try {
					const companyId = getCompanyIdFromIntervention(intervention);
					const pythonData = {
						payment_method: paiementForm.methode_paiement,
						amount: montant,
						date_payment:
							paiementForm.date_payment || new Date().toISOString().split('T')[0],
						currency: 'EUR',
						payment_type: 'inbound',
						partner_type: 'customer',
						partner_id: intervention?.client?._id,
						partner_name: intervention?.client?.nom,
						company_id: companyId,
						ref_intervention: intervention.reference || '',
						notes: paiementForm.notes || '',
					};

					const res = await paiementAPI(pythonData);
					pythonPaymentId = res?.id;

					if (pythonPaymentId) {
						showNotification('Succès', 'Paiement comptabilisé', 'success');
					}
				} catch (pythonError) {
					showNotification(
						'Info',
						'Paiement créé localement (Erreur sync compta)',
						'warning',
					);
				}
			}

			const paiementLocalData = {
				montant,
				methode_paiement: paiementForm.methode_paiement,
				date_payment: paiementForm.date_payment || new Date().toISOString().split('T')[0],
				ref_intervention: paiementForm.reference || '',
				statut: paiementForm.statut || 'en_attente',
				notes: paiementForm.notes || '',
				currency: 'EUR',
				payment_type: 'inbound',
				python_payment_id: pythonPaymentId,
				created_at: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			};

			if (editingPaiement && editingPaiement._id) {
				const updatedPaiement = {
					...editingPaiement,
					...paiementLocalData,
					_id: editingPaiement._id,
				};
				setPaiements((prev) =>
					prev.map((p) => (p._id === editingPaiement._id ? updatedPaiement : p)),
				);
				showNotification('Succès', 'Paiement mis à jour', 'success');
			} else {
				const newPaiement = {
					_id: pythonPaymentId || `temp-${Date.now()}`,
					...paiementLocalData,
				};
				setPaiements((prev) => [...prev, newPaiement]);
				showNotification('Succès', 'Paiement ajouté', 'success');
			}

			setShowPaiementModal(false);
			setEditingPaiement(null);
		} catch (error) {
			console.error('❌ Error saving payment:', error);
			setError('Erreur lors de la sauvegarde');
			showNotification('Erreur', 'Erreur inattendue lors de la sauvegarde', 'danger');
		} finally {
			setSubmittingPaiement(false);
		}
	};

	const handleDeletePaiement = async (paiementId) => {
		if (window.confirm('Confirmer la suppression définitive ?')) {
			try {
				await deletePythonAPI('paiements', `/${paiementId}`);
			} catch (error) { }
			setPaiements((prev) => prev.filter((p) => p._id !== paiementId));

			showNotification('Information', 'Paiement supprimé', 'info');
		}
	};

	// --- GESTION PRINCIPALE ---
	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError('');

		try {
			if (!formData.description_travaux.trim()) throw new Error('La description est requise');

			const devisIds = devisList.map((devis) => getDevisId(devis)).filter((id) => id);

			// Calculate totals
			const totalPaiements = paiements.reduce(
				(sum, p) => sum + (parseFloat(p.montant) || 0),
				0,
			);
			const totalPaiementsValides = paiements
				.filter((p) => p.statut === PAIEMENT_STATUTS.VALIDE)
				.reduce((sum, p) => sum + (parseFloat(p.montant) || 0), 0);

			const rapportData = {
				description_travaux: formData.description_travaux,
				devis: devisIds,
				total_paiements: totalPaiements,
				total_paiements_valides: totalPaiementsValides,
				voiceRecordings: rapport?.voiceRecordings || [],
				attachments: attachments,
				paiements: paiements,
				created_at: rapport?.created_at || new Date().toISOString(),
				updatedAt: new Date().toISOString(),
				pose_faite: formData.pose_faite,
				pose_sur_place: formData.pose_sur_place
			};

			// Upload any pending attachments
			if (filesToUpload.length > 0) {
				const uploadResult = await uploadAttachments();
				if (uploadResult.success) {
					rapportData.attachments = [...attachments, ...uploadResult.uploadedFiles];
				}
			}

			const savedRapport = await onSave(rapportData);
			/* 
	  if (!savedRapport) {
		throw new Error("Impossible de sauvegarder le rapport");
	  } */

			showNotification(
				'Succès',
				`Rapport ${isEditing ? 'mis à jour' : 'créé'} avec ${devisIds.length
				} devis associé(s)`,
				'success',
			);

			reloadData?.();
			onHide();
			return savedRapport;
		} catch (err) {
			console.error('❌ Error saving rapport:', err);
			setError(err.message);
			showNotification('Erreur', err.message, 'danger');
			throw err;
		} finally {
			setIsSubmitting(false);
		}
	};

	// --- GESTION DEVIS ---
	const handleDevisSuccess = (devis) => {
		const devisId = getDevisId(devis);
		if (devisId) {
			if (selectedDevisToEdit) {
				setDevisList((prev) =>
					prev.map((d) =>
						getDevisId(d) === getDevisId(selectedDevisToEdit) ? devis : d,
					),
				);
				showNotification('Devis', 'Devis mis à jour', 'success');
			} else {
				handleAddDevis({
					id: devisId,
					name: getDevisName(devis),
					totalTTC: devis.totalTTC || 0,
					dateValidite: devis.dateValidite,
					statut: devis.statut || 'brouillon',
				});
				showNotification('Devis', 'Devis créé et ajouté au rapport', 'success');
			}
			setSelectedDevisToEdit(null);
			setShowDevisModal(false);
		}
	};

	// --- FORMATTERS & HELPERS ---
	const formatMontant = (m) =>
		new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(m || 0);
	const formatDate = (d) =>
		d
			? new Date(d).toLocaleDateString('fr-FR', {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
			})
			: '-';

	const getBadgeStatus = (status) => {
		switch (status) {
			case PAIEMENT_STATUTS.VALIDE:
				return 'success';
			case PAIEMENT_STATUTS.REFUSE:
				return 'danger';
			case PAIEMENT_STATUTS.EN_ATTENTE:
				return 'primary';
			default:
				return 'light text-dark border';
		}
	};

	const getPaymentIconName = (methode) => {
		switch (methode) {
			case METHODES_PAIEMENT.ESPECES:
				return 'LocalAtm';
			case METHODES_PAIEMENT.CARTE_BANCAIRE:
				return 'CreditCard';
			case METHODES_PAIEMENT.CHEQUE:
				return 'RequestQuote';
			case METHODES_PAIEMENT.VIREMENT:
				return 'AccountBalance';
			default:
				return 'AccountBalanceWallet';
		}
	};

	const renderUploadButton = () => {
		return (
			<Button
				variant='primary'
				onClick={handleUploadPendingAttachments}
				disabled={uploadingAttachments || filesToUpload.length === 0}
				size='sm'
				className='w-100 w-sm-auto'>
				{uploadingAttachments ? (
					<>
						<span
							className='spinner-border spinner-border-sm me-2'
							role='status'></span>
						Téléchargement...
					</>
				) : (
					<>
						<Icon icon='CloudUpload' className='me-2' />
						Ajouter {filesToUpload.length} fichier(s)
					</>
				)}
			</Button>
		);
	};

	// --- RESPONSIVE STYLES ---
	const customTabStyles = `
    .nav-tabs .nav-link {
      color: #6c757d;
      border: none;
      border-bottom: 3px solid transparent;
      padding-bottom: 1rem;
      margin-right: 1.5rem;
      font-size: 0.875rem;
    }
    .nav-tabs .nav-link.active {
      color: #0d6efd !important;
      font-weight: bold;
      border-bottom: 3px solid #0d6efd;
      background-color: transparent;
    }
    .nav-tabs .nav-link:hover {
      border-color: transparent;
      color: #0d6efd;
    }
    
    @media (max-width: 768px) {
      .nav-tabs .nav-link {
        margin-right: 0.75rem;
        padding: 0.5rem 0.75rem;
        font-size: 0.8rem;
      }
      .nav-tabs .nav-link span {
        display: block;
        margin-bottom: 0.25rem;
      }
    }
    
    @media (max-width: 576px) {
      .nav-tabs {
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }
      .nav-tabs .nav-link {
        white-space: nowrap;
        flex-shrink: 0;
      }
    }
    
    .btn-action-icon {
      transition: transform 0.2s;
    }
    .btn-action-icon:hover {
      transform: scale(1.1);
    }
    .gallery-card {
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .gallery-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 6px 12px rgba(0,0,0,0.15) !important;
    }
    .object-fit-cover {
      object-fit: cover;
    }
    .upload-zone {
      border: 2px dashed #dee2e6;
      border-radius: 12px;
      transition: border-color 0.3s;
    }
    .upload-zone:hover {
      border-color: #0d6efd;
      background-color: rgba(13, 110, 253, 0.05);
    }
    .devis-list .card {
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .devis-list .card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }
    
    /* Responsive table styles */
    @media (max-width: 768px) {
      .table-responsive-sm {
        font-size: 0.875rem;
      }
      .table-responsive-sm th,
      .table-responsive-sm td {
        padding: 0.5rem;
      }
    }
    
    /* Responsive button groups */
    @media (max-width: 576px) {
      .btn-group-responsive {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .btn-group-responsive .btn {
        width: 100%;
      }
    }
    
    /* Responsive modal footer */
    @media (max-width: 576px) {
      .modal-footer-responsive {
        flex-direction: column;
        gap: 1rem;
      }
      .modal-footer-responsive .btn {
        width: 100%;
      }
    }
    
    /* Responsive card headers */
    @media (max-width: 768px) {
      .card-header-responsive {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 1rem;
      }
      .card-header-responsive .btn-group {
        align-self: stretch;
      }
      .card-header-responsive .btn {
        width: 100%;
      }
    }
    
    /* Responsive form controls */
    @media (max-width: 576px) {
      .form-control-responsive {
        font-size: 0.875rem;
        padding: 0.5rem 0.75rem;
      }
    }
  `;

	return (
		<>
			<style>{customTabStyles}</style>

			{/* Main Modal */}
			<Modal
				show={show}
				onHide={onHide}
				size='xl'
				centered
				className='nv-2'
				backdrop='static'
				contentClassName='border-0 shadow-lg rounded-4 overflow-hidden'
				dialogClassName='my-0'
				scrollable>
				<Modal.Header className='bg-white border-bottom pt-4 px-3 px-md-4 pb-0 d-block'>
					<Nav
						variant='tabs'
						className='border-0 mt-3'
						activeKey={activeTab}
						onSelect={setActiveTab}>
						<Nav.Item>
							<Nav.Link eventKey='general'>
								<span className='me-2 d-inline-block d-md-inline'>
									<Icon icon='Info' size='lg' />
								</span>
								<span className='d-none d-md-inline'>Rapport</span>
								<span className='d-md-none'>Rapport</span>
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey='finance'>
								<span className='me-2 d-inline-block d-md-inline'>
									<Icon icon='AccountBalanceWallet' size='lg' />
								</span>
								<span className='d-none d-md-inline'>Devis & Paiements</span>
								<span className='d-md-none'>Finance</span>
							</Nav.Link>
						</Nav.Item>
					</Nav>
				</Modal.Header>

				<Form onSubmit={handleSubmit} className='d-flex flex-column h-100 bg-light'>
					<Modal.Body
						className='p-3 p-md-4'
						style={{
							minHeight: '50vh',
							maxHeight: 'calc(100vh - 200px)',
							overflowY: 'auto',
							WebkitOverflowScrolling: 'touch',
						}}>
						{error && (
							<Alert
								variant='danger'
								className='rounded-3 shadow-sm border-0 mb-4 bg-white text-danger'>
								<span className='me-2'>
									<Icon icon='Error' size='lg' />
								</span>
								{error}
							</Alert>
						)}

						<div className='tab-content'>
							{/* --- ONGLET RAPPORT (Description + Photos) --- */}
							{activeTab === 'general' && (
								<div className='tab-pane fade show active'>
									<Row className='g-3 g-md-4'>
										<Col xs={12}>

											<Card className='border-0 shadow-sm rounded-4 mb-4'>
												<Card.Body className='p-3 p-md-4'>
													{/* STATUT DE LA POSE (Si type === 'pose') */}
													{intervention?.type === 'pose' && (
														<div className="mb-4">
															<label className="fw-bold text-primary mb-2 text-uppercase small ls-1 d-block">
																Statut de la pose
															</label>
															<Row className="g-2">
																<Col xs={6}>
																	<label className={`w-100 p-3 border rounded-3 d-flex align-items-center cursor-pointer transition-all ${formData?.pose_faite ? 'bg-primary text-white border-primary shadow-sm' : 'bg-light text-muted'}`}>
																		<input
																			type="radio"
																			className="me-2 form-check-input"
																			name="pose_faite"
																			checked={!!formData?.pose_faite}
																			onChange={() => {
																				const change = { pose_faite: true };
																				setFormData(prev => ({ ...prev, ...change }));
																			}}
																		/>
																		<span className="fw-bold">Pose faite</span>
																	</label>
																</Col>
																<Col xs={6}>
																	<label className={`w-100 p-3 border rounded-3 d-flex align-items-center cursor-pointer transition-all ${!formData?.pose_faite ? 'bg-primary text-white border-primary shadow-sm' : 'bg-light text-muted'}`}>
																		<input
																			type="radio"
																			className="me-2 form-check-input"
																			name="pose_faite"
																			checked={!formData?.pose_faite}
																			onChange={() => {
																				const change = { pose_faite: false };
																				setFormData(prev => ({ ...prev, ...change }));
																			}}
																		/>
																		<span className="fw-bold">En cours</span>
																	</label>
																</Col>
															</Row>
														</div>
													)}

													{/* DEVIS STATUS (Si type === 'devis') */}
													{intervention?.type === 'devis' && (
														<div className='mb-4'>
															<label className={`w-100 p-3 border rounded-3 d-flex align-items-center cursor-pointer transition-all ${formData?.pose_sur_place ? 'bg-soft-success border-success text-success' : 'bg-light'}`}>
																<input
																	type='checkbox'
																	className="me-3 form-check-input"
																	style={{ width: '22px', height: '22px' }}
																	checked={!!formData?.pose_sur_place}
																	onChange={(e) => {
																		const isChecked = e.target.checked;
																		const change = { pose_sur_place: isChecked };
																		setFormData(prev => ({ ...prev, ...change }));
																	}}
																/>
																<span className="fw-bold">Devis accepté et Pose sur place</span>
															</label>
														</div>
													)}

													<hr className="my-4 opacity-25" />

													{/* DESCRIPTION DES TRAVAUX */}
													<label className='fw-bold text-primary mb-3 text-uppercase small ls-1 d-block'>
														Description des travaux
													</label>
													<Form.Control
														as='textarea'
														rows={6}
														value={formData.description_travaux}
														onChange={(e) =>
															setFormData((prev) => ({
																...prev,
																description_travaux: e.target.value,
															}))
														}
														placeholder='Décrivez les actions effectuées...'
														className='bg-light border-0 rounded-3 p-3 text-dark'
														style={{
															resize: 'none',
															minHeight: '150px',
														}}
														required
													/>
													<Form.Text className='text-muted mt-2 d-block'>
														<i className="fas fa-info-circle me-1"></i>
														Cette description est requise pour pouvoir terminer l'intervention.
													</Form.Text>
												</Card.Body>
											</Card>
										</Col>

										<Col xs={12}>
											<Card className='border-0 shadow-sm rounded-4 bg-white h-100'>
												<Card.Header className='bg-white py-3 border-bottom d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center card-header-responsive'>
													<div className='d-flex align-items-center mb-2 mb-md-0'>
														<Icon
															icon='Photo'
															className='me-2 text-primary'
														/>
														<h5 className='mb-0 fw-bold text-dark fs-6 fs-md-5'>
															Photos
														</h5>
														<Badge
															bg='info'
															className='ms-2 rounded-pill'>
															{attachments.length +
																filesToUpload.length}
														</Badge>
													</div>
													<div className='d-flex align-items-center gap-2'>
														<label className='btn btn-primary btn-sm mb-0 cursor-pointer '>
															<Icon icon='Plus' className='me-1' />
															<span className='d-none d-md-inline'>
																Ajouter des photos
															</span>
															<span className='d-md-none'>
																Ajouter
															</span>
															<input
																type='file'
																multiple
																onChange={handleFileChange}
																style={{ display: 'none' }}

															/>
														</label>
													</div>
												</Card.Header>
												<Card.Body className='p-3 p-md-4'>
													{/* Files to upload section */}
													{filesToUpload.length > 0 && (
														<div className='mb-4'>
															<h6 className='text-primary fw-bold mb-3 fs-6'>
																<Icon
																	icon='Upload'
																	className='me-2'
																/>
																Photos à ajouter (
																{filesToUpload.length})
															</h6>

															<div className='mb-3 p-3 rounded-4 upload-zone'>
																<Row className='g-2 g-md-3'>
																	{filesToUpload.map(
																		(file, index) => (
																			<Col
																				xs={6}
																				sm={4}
																				md={3}
																				lg={2}
																				xl={2}
																				key={index}>
																				{renderFileItem(
																					file,
																					index,
																					true,
																				)}
																			</Col>
																		),
																	)}
																</Row>
															</div>

															<div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2'>
																<div className=''>
																	{renderUploadButton()}
																</div>
															</div>
														</div>
													)}

													{/* Attached photos gallery */}
													<div>
														<h6 className='text-success fw-bold mb-3 fs-6'>
															<Icon
																icon='PhotoLibrary'
																className='me-2'
															/>
															Photos ajoutées ({attachments.length})
														</h6>

														{attachments.length > 0 ? (
															<Row className='g-2 g-md-3'>
																{attachments.map((att, index) => (
																	<Col
																		xs={6}
																		sm={4}
																		md={3}
																		lg={2}
																		xl={2}
																		key={index}>
																		{renderFileItem(
																			att,
																			index,
																			false,
																		)}
																	</Col>
																))}
															</Row>
														) : (
															<div className='text-center py-5 text-muted rounded-4'>
																<Icon
																	icon='PhotoCamera'
																	size='lg'
																	className='mb-3 opacity-25'
																/>
																<div className='fw-medium'>
																	Aucune photo ajoutée
																</div>
																<small className='text-muted d-block d-md-none'>
																	Cliquez sur "Ajouter" pour
																	ajouter des photos
																</small>
																<small className='text-muted d-none d-md-block'>
																	Ajoutez des photos en cliquant
																	sur "Ajouter des photos"
																</small>
															</div>
														)}
													</div>
												</Card.Body>
											</Card>
										</Col>
									</Row>
								</div>
							)}

							{/* --- ONGLET FINANCE --- */}
							{activeTab === 'finance' && (
								<div className='tab-pane fade show active'>
									{/* DEVIS SECTION */}
									<Card className='border-0 shadow-sm rounded-4 bg-white mb-4'>
										<Card.Body className='p-3 p-md-4'>
											<div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2'>
												<h5 className='fw-bold mb-0 text-dark fs-6 fs-md-5'>
													Devis associés ({devisList.length})
												</h5>
												<div className='d-flex gap-2'>
													<Button
														variant='primary'
														size='sm'
														className='rounded-pill px-3 '
														onClick={() => setShowDevisModal(true)}>
														<span className='me-2'>
															<Icon icon='Add' size='lg' />
														</span>
														<span className='d-none d-md-inline'>
															Créer un devis
														</span>
														<span className='d-md-none'>
															Nouveau devis
														</span>
													</Button>
												</div>
											</div>

											{devisList.length > 0 ? (
												<div className='devis-list'>
													{devisList.map((devis, index) => {
														const devisId = getDevisId(devis);
														const devisName = getDevisName(devis);
														const devisStatut =
															devis.statut || 'inconnu';
														const devisMontant = devis.totalTTC || 0;
														const devisDateValidite =
															devis.dateValidite;

														return (
															<div
																key={devisId || index}
																className='mb-3'>
																<Card className='border border-light-subtle rounded-3'>
																	<Card.Body className='p-2 p-md-3'>
																		<div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2'>
																			<div className='d-flex align-items-center flex-grow-1 w-100'>
																				<div className='bg-primary bg-opacity-10 p-2 rounded-circle me-3 d-none d-md-flex'>
																					<Icon
																						icon='Description'
																						className='text-primary'
																					/>
																				</div>
																				<div className='flex-grow-1'>
																					<div className='d-flex align-items-center mb-1'>
																						<Button
																							variant='link'
																							className='text-primary p-0 me-2 text-decoration-none'
																							onClick={() =>
																								window.open(
																									`/devis/${devisId}`,
																									'_blank',
																								)
																							}
																							title='Ouvrir le devis'>
																							<Icon
																								icon='Visibility'
																								size='md'
																							/>
																						</Button>
																						<h6 className='fw-bold mb-0 fs-6 text-truncate'>
																							{
																								devisName
																							}
																						</h6>
																						<Badge
																							bg={
																								devisStatut ===
																									'validé'
																									? 'success'
																									: 'warning'
																							}
																							className='ms-2 rounded-pill d-none d-md-inline-block'>
																							{
																								devisStatut
																							}
																						</Badge>
																					</div>
																					<small className='text-muted d-block d-md-none mb-1'>
																						<Badge
																							bg={
																								devisStatut ===
																									'validé'
																									? 'success'
																									: 'warning'
																							}
																							className='rounded-pill'>
																							{
																								devisStatut
																							}
																						</Badge>
																					</small>
																					<small className='text-muted d-block'>
																						Validité:{' '}
																						{formatDate(
																							devisDateValidite,
																						)}
																					</small>
																				</div>
																				<div className='text-end ms-3 d-none d-md-block'>
																					<div className='h5 fw-bold text-dark mb-0'>
																						{formatMontant(
																							devisMontant,
																						)}
																					</div>
																					<small className='text-muted'>
																						TTC
																					</small>
																				</div>
																			</div>
																			<div className='d-flex justify-content-between w-100 w-md-auto'>
																				<div className='d-md-none text-start'>
																					<div className='h6 fw-bold text-dark mb-0'>
																						{formatMontant(
																							devisMontant,
																						)}
																					</div>
																					<small className='text-muted'>
																						TTC
																					</small>
																				</div>
																				<div className='d-flex'>
																					<Button
																						variant='link'
																						className='text-primary ms-2 p-0'
																						onClick={() =>
																							handleEditDevis(
																								devis,
																							)
																						}
																						title='Modifier'>
																						<Icon
																							icon='Edit'
																							size='md'
																						/>
																					</Button>
																					<Button
																						variant='link'
																						className='text-danger ms-2 p-0'
																						onClick={() =>
																							handleRemoveDevis(
																								devisId,
																							)
																						}
																						title='Retirer'>
																						<Icon
																							icon='Close'
																							size='lg'
																						/>
																					</Button>
																				</div>
																			</div>
																		</div>
																	</Card.Body>
																</Card>
															</div>
														);
													})}
												</div>
											) : (
												<div className='text-center py-4 text-muted rounded-4 border border-light-subtle border-dashed'>
													<Icon
														icon='Description'
														size='lg'
														className='mb-3 opacity-25'
													/>
													<div className='fw-medium'>
														Aucun devis associé
													</div>
													<small className='text-muted'>
														Cliquez sur "Créer un devis" pour créer un
														nouveau devis
													</small>
												</div>
											)}
										</Card.Body>
									</Card>

								
								</div>
							)}
						</div>
					</Modal.Body>

					{/* FOOTER ACTIONS */}
					<Modal.Footer className='bg-white border-top p-3 p-md-4 modal-footer-responsive'>
						<Container className='d-flex flex-column flex-md-row justify-content-between align-items-center p-0 gap-2'>
							<div className='w-100 w-md-auto'>
								{isEditing && (
									<Button
										variant='link'
										className='text-danger text-decoration-none px-0 opacity-75 hover-opacity-100 w-100 w-md-auto text-center text-md-start'
										onClick={() => onDelete()}>
										<span className='me-2'>
											<Icon icon='Delete' size='lg' />
										</span>
										Supprimer le rapport
									</Button>
								)}
							</div>
							<div className='d-flex flex-column flex-md-row gap-2 w-100 w-md-auto'>
								<Button
									variant='light'
									className='px-4 py-2 border-0 fw-medium w-100 w-md-auto'
									onClick={onHide}>
									Fermer
								</Button>
								<Button
									variant='primary'
									type='submit'
									className='px-5 py-2 fw-bold shadow-sm rounded-pill d-flex align-items-center justify-content-center w-100 w-md-auto'
									disabled={isSubmitting}>
									{isSubmitting && <Spinner size='sm' className='me-2' />}
									<span>{isEditing ? 'Mettre à jour' : 'Créer le rapport'}</span>
								</Button>
							</div>
						</Container>
					</Modal.Footer>
				</Form>
			</Modal>

		

			{/* --- MODAL DEVIS --- */}
			<CreateEditDevisModal
				isOpen={showDevisModal}
				setIsOpen={setShowDevisModal}
				onSuccess={handleDevisSuccess}
				onClose={() => {
					setShowDevisModal(false);
					setSelectedDevisToEdit(null);
				}}
				interventionClient={intervention?.client}
				intervention={intervention || {}}
				devisId={selectedDevisToEdit ? getDevisId(selectedDevisToEdit) : null}
				devisData={selectedDevisToEdit}
			/>
		</>
	);
};

export default RapportModal;
